import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/components/ui/use-toast";

const DemoVideoCallPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Change page title to "Hirevox" for demo video call page
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Hirevox";

    return () => {
      document.title = originalTitle;
    };
  }, []);

  useEffect(() => {
    const createDemoVideoCall = async () => {
      if (!user || !currentOrganization) {
        setError("Please log in and select an organization");
        setLoading(false);
        return;
      }

      try {
        // First, find or create the lead
        const email = "hari@f22labs.com";
        const { data: existingLead, error: findError } = await supabase
          .from("leads")
          .select("*")
          .eq("email", email)
          .eq("organization_id", currentOrganization.id)
          .single();

        let leadId: string;

        if (findError && findError.code !== "PGRST116") {
          // PGRST116 is "no rows returned"
          throw findError;
        }

        if (existingLead) {
          leadId = existingLead.id;
        } else {
          // Create new lead
          const { data: newLead, error: createError } = await supabase
            .from("leads")
            .insert({
              first_name: "Hari",
              last_name: "F22Labs",
              email: email,
              phone_number: "+1234567890", // Default phone number
              organization_id: currentOrganization.id,
            })
            .select()
            .single();

          if (createError) throw createError;
          leadId = newLead.id;
        }

        // Find the SCREENING assistant
        const { data: assistants, error: assistantError } = await supabase
          .from("assistants")
          .select("*")
          .eq("organization_id", currentOrganization.id)
          .ilike("name", "%SCREENING%");

        if (assistantError) throw assistantError;

        if (!assistants || assistants.length === 0) {
          throw new Error("No SCREENING assistant found. Please create one first.");
        }

        const screeningAssistant = assistants[0];

        // Generate video call link using the API
        const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000';
        const response = await fetch(`${CALL_SERVICE_URL}/api/video-link`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: leadId,
            agent_id: screeningAssistant.id,
            proctoring_tools: {
              tab_change_detection: true,
              screen_share: true,
              multiple_monitor_detection: false,
              face_out_of_view: false,
              face_blur_detection: false,
            },
            organization_id: currentOrganization.id,
            echo_cancellation: true,
            metadata: {
              generated_by: user.id,
              generated_at: new Date().toISOString(),
              demo: true,
            }
          }),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Failed to generate video link: ${errorData}`);
        }

        const data = await response.json();
        
        // Redirect to the generated video call link
        navigate(`/video-call/${data.token}`);
        
      } catch (err) {
        console.error("Error creating demo video call:", err);
        setError(err instanceof Error ? err.message : "Failed to create demo video call");
        toast({
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to create demo video call",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    createDemoVideoCall();
  }, [user, currentOrganization, navigate, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner className="mb-4" />
          <p className="text-lg">Creating demo video call...</p>
          <p className="text-sm text-gray-500 mt-2">
            Setting up video call for hari@f22labs.com with SCREENING agent
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => navigate("/leads")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Leads
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default DemoVideoCallPage;